import React, { useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, XIcon } from 'lucide-react';
import { Card } from '../components/Card';
import { TrustScore } from '../components/TrustScore';
import { Button } from '../components/Button';
import { exchanges } from '../data/mockData';
export function CompareExchangesPage() {
  const [searchParams] = useSearchParams();
  const ids = searchParams.get('ids')?.split(',') || [];
  const selectedExchanges = useMemo(() => {
    return exchanges.filter(e => ids.includes(e.id)).slice(0, 3);
  }, [ids]);
  if (selectedExchanges.length < 2) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-id="element-178">
        <div className="text-center" data-id="element-179">
          <h1 className="text-2xl font-bold mb-4" data-id="element-180">
            Please select at least 2 exchanges to compare
          </h1>
          <Link to="/exchanges" data-id="element-181">
            <Button data-id="element-182">Back to Exchanges</Button>
          </Link>
        </div>
      </div>;
  }
  const ComparisonRow = ({
    label,
    values,
    highlight = false
  }: {
    label: string;
    values: (string | number | React.ReactNode)[];
    highlight?: boolean;
  }) => <div className={`grid grid-cols-${selectedExchanges.length + 1} gap-4 py-4 border-b border-gray-200 ${highlight ? 'bg-blue-50' : ''}`} data-id="element-183">
      <div className="font-semibold text-gray-900" data-id="element-184">{label}</div>
      {values.map((value, index) => <div key={index} className="text-gray-700" data-id="element-185">
          {value}
        </div>)}
    </div>;
  return <div className="min-h-screen bg-slate-50" data-id="element-186">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-blue-900 text-white py-12" data-id="element-187">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-188">
          <Link to="/exchanges" className="inline-flex items-center gap-2 text-slate-300 hover:text-white mb-6 transition-colors" data-id="element-189">
            <ArrowLeftIcon size={20} data-id="element-190" />
            <span data-id="element-191">Back to Exchanges</span>
          </Link>
          <h1 className="text-4xl font-bold mb-3" data-id="element-192">Exchange Comparison</h1>
          <p className="text-slate-300 text-lg" data-id="element-193">
            Side-by-side comparison of {selectedExchanges.length} exchanges
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-194">
        {/* Exchange Headers */}
        <div className={`grid grid-cols-${selectedExchanges.length + 1} gap-4 mb-8`} data-id="element-195">
          <div data-id="element-196"></div>
          {selectedExchanges.map(exchange => <Card key={exchange.id} className="p-6 text-center" data-id="element-197">
              <img src={exchange.logo} alt={exchange.name} className="w-16 h-16 mx-auto mb-4" data-id="element-198" />
              <h3 className="font-bold text-xl mb-2" data-id="element-199">{exchange.name}</h3>
              <TrustScore score={exchange.trustScore} size="sm" data-id="element-200" />
              <div className="mt-4" data-id="element-201">
                <a href={exchange.website} target="_blank" rel="noopener noreferrer" data-id="element-202">
                  <Button size="sm" className="w-full" data-id="element-203">
                    Visit Exchange
                  </Button>
                </a>
              </div>
            </Card>)}
        </div>

        {/* Comparison Table */}
        <Card className="p-6" data-id="element-204">
          <h2 className="text-2xl font-bold mb-6" data-id="element-205">Detailed Comparison</h2>

          <div className="overflow-x-auto" data-id="element-206">
            <ComparisonRow label="Trust Score" values={selectedExchanges.map(e => <div className="flex items-center gap-2" data-id="element-208">
                  <span className="text-2xl font-bold text-blue-600" data-id="element-209">
                    {e.trustScore.toFixed(1)}
                  </span>
                  <span className="text-gray-500" data-id="element-210">/10</span>
                </div>)} highlight data-id="element-207" />

            <ComparisonRow label="24h Trading Volume" values={selectedExchanges.map(e => `$${(e.tradingVolume / 1000000000).toFixed(2)}B`)} data-id="element-211" />

            <ComparisonRow label="Supported Coins" values={selectedExchanges.map(e => e.supportedCoins.toLocaleString())} data-id="element-212" />

            <ComparisonRow label="Trading Fee" values={selectedExchanges.map(e => e.tradingFee)} highlight data-id="element-213" />

            <ComparisonRow label="Withdrawal Fee" values={selectedExchanges.map(e => e.withdrawalFee)} data-id="element-214" />

            <ComparisonRow label="Rank" values={selectedExchanges.map(e => `#${e.rank}`)} data-id="element-215" />

            <ComparisonRow label="User Reviews" values={selectedExchanges.map(e => `${e.reviews.length} reviews`)} data-id="element-216" />
          </div>

          {/* Features Comparison */}
          <div className="mt-8" data-id="element-217">
            <h3 className="text-xl font-bold mb-4" data-id="element-218">Features</h3>
            <div className="space-y-3" data-id="element-219">
              {Array.from(new Set(selectedExchanges.flatMap(e => e.features))).map(feature => <div key={feature} className={`grid grid-cols-${selectedExchanges.length + 1} gap-4 py-2`} data-id="element-220">
                  <div className="text-gray-700" data-id="element-221">{feature}</div>
                  {selectedExchanges.map(exchange => <div key={exchange.id} className="flex justify-center" data-id="element-222">
                      {exchange.features.includes(feature) ? <CheckIcon className="text-green-600" size={20} data-id="element-223" /> : <XIcon className="text-gray-300" size={20} data-id="element-224" />}
                    </div>)}
                </div>)}
            </div>
          </div>

          {/* Pros & Cons */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-225">
            {selectedExchanges.map(exchange => <div key={exchange.id} data-id="element-226">
                <h4 className="font-bold mb-3" data-id="element-227">{exchange.name}</h4>

                <div className="mb-4" data-id="element-228">
                  <div className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-1" data-id="element-229">
                    <CheckIcon size={16} data-id="element-230" />
                    Pros
                  </div>
                  <ul className="space-y-1" data-id="element-231">
                    {exchange.pros.map((pro, index) => <li key={index} className="text-sm text-gray-700 flex items-start gap-2" data-id="element-232">
                        <span className="text-green-600 mt-0.5" data-id="element-233">•</span>
                        {pro}
                      </li>)}
                  </ul>
                </div>

                <div data-id="element-234">
                  <div className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1" data-id="element-235">
                    <XIcon size={16} data-id="element-236" />
                    Cons
                  </div>
                  <ul className="space-y-1" data-id="element-237">
                    {exchange.cons.map((con, index) => <li key={index} className="text-sm text-gray-700 flex items-start gap-2" data-id="element-238">
                        <span className="text-red-600 mt-0.5" data-id="element-239">•</span>
                        {con}
                      </li>)}
                  </ul>
                </div>
              </div>)}
          </div>
        </Card>

        {/* Bottom CTA */}
        <div className="mt-8 text-center" data-id="element-240">
          <Link to="/exchanges" data-id="element-241">
            <Button variant="outline" size="lg" data-id="element-242">
              Compare Different Exchanges
            </Button>
          </Link>
        </div>
      </div>
    </div>;
}